import { useEffect, useState } from "react";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { FaroButton } from "@components/common/faro-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseProjectIdProps } from "@custom-types/sdb-company-types";
import { fetchRevisionsAndDraftEntities } from "@hooks/data-management/use-data-management";
import { WorkflowState } from "@pages/data-management/data-management-types";
import { PublishButton } from "@pages/data-management/publish-button";
import { areProjectPointCloudAndMapOutdated } from "@pages/data-management/publish-utils";
import { useAppDispatch } from "@store/store-helper";
import { DataManagementEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props extends BaseProjectIdProps {
  /** Workflow state of the project */
  state: WorkflowState;
  /** Flag if the button is only visible because of dev mode. */
  isDevMode: boolean;
  /** Callback to set the flag whether the publish panel is visible. */
  setIsPublishPanelOpen: React.Dispatch<React.SetStateAction<boolean>>,
}

/**
 * Returns the Publish button if the point cloud and map are up-to-date.
 * Otherwise returns the button to open the publish panel that offers publish options.
 */
export function OpenPublishPanelButton({
  projectId,
  state,
  isDevMode,
  setIsPublishPanelOpen,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const projectApiClient = useProjectApiClient({ projectId });
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [shouldShowPanel, setShouldShowPanel] = useState<boolean>(false);

  function openPublishPanel(): void {
    trackEvent({ name: DataManagementEvents.publishPanel });
    setIsPublishPanelOpen(true);
  }

  useEffect(() => {
    async function fetchState(): Promise<void> {
      try {
        setIsLoading(true);

        const revisionsAndDraftEntities = await fetchRevisionsAndDraftEntities(dispatch, projectApiClient);
        const shouldShowPanel = !!revisionsAndDraftEntities.openDraftRevision &&
          areProjectPointCloudAndMapOutdated(
            revisionsAndDraftEntities.openDraftEntities,
            revisionsAndDraftEntities.openDraftRevision,
            revisionsAndDraftEntities.revisions
          );
        setShouldShowPanel(shouldShowPanel);
      } catch (error) {
        handleErrorWithToast({
          id: `OpenPublishPanelButton-fetchState-${new Date().toString()}`,
          error,
          title: "Failed to fetch latest project state. Please reload the page to try again.",
        });
      } finally {
        setIsLoading(false);
      }
    }

    void fetchState();
  }, [dispatch, handleErrorWithToast, projectApiClient]);

  const isDisabled = state === "registerBadResult" && !isDevMode;
  const tooltipBadResult = "Your scans weren’t automatically registered. " +
    "Check each scan position and adjust them manually in the registration editor before publishing.";

  if (shouldShowPanel) {
    return (
      <SphereTooltip
        title={isDisabled ? tooltipBadResult : "Set options and publish the project."}
        enterDelay={isDisabled ? 0 : undefined}
        dataTestId="sa-open-publish-tooltip"
      >
        <FaroButton
          onClick={openPublishPanel}
          isLoading={isLoading}
          isDisabled={isDisabled}
        >
          Publish
        </FaroButton>
      </SphereTooltip>
    );
  } else {
    return (
      <PublishButton
        projectId={projectId}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isFromPanel={false}
        tooltip={isDisabled ? tooltipBadResult : "Publish the project." }
      />
    );
  }
}
